/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

export function clampVoiceVolumePercent(value: number): number {
	if (!Number.isFinite(value)) {
		return 100;
	}

	return Math.max(0, Math.min(200, value));
}

export function voiceVolumePercentToTrackVolume(value: number): number {
	return Math.max(0, Math.min(1, clampVoiceVolumePercent(value) / 100));
}

const VOICE_VOLUME_MAX_PERCENT = 200;

// Stevens' power law: perceived loudness ∝ amplitude^0.6 (exponent 0.3
// for intensity, doubled for amplitude). Inverting gives gain = position^(1/0.6)
// ≈ 1.667. We use 1.5 as a practical compromise common in audio software —
// slightly gentler than the pure psychoacoustic curve.
const PERCEPTUAL_EXPONENT = 1.5;

export function boostedVoiceVolumePercentToTrackVolume(value: number): number {
	const clamped = clampVoiceVolumePercent(value);
	if (clamped === 0) {
		return 0;
	}

	const normalized = clamped / VOICE_VOLUME_MAX_PERCENT;
	return normalized ** PERCEPTUAL_EXPONENT;
}
